import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Chip, TextField, InputAdornment, Pagination,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, FormControl, InputLabel, Select, MenuItem, CircularProgress,
  Divider
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import AddIcon from "@mui/icons-material/Add";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useNavigate } from "react-router-dom";
import { fetchData, postData, deleteData, putData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";
import ConfirmDialog from "../../DynamicComponent/ConfirmDialog";

const PURPLE = "#244C77";
const SECONDARY = "#4A7BA8";

const DOC_TYPES = ["Handbook", "Guideline", "Newsletter", "Curriculum", "Other"];

const STATUS_CONFIG = {
  published: { label: "Published", color: "#16a34a", bg: "#f0fdf4" },
  draft:     { label: "Draft",     color: "#d97706", bg: "#fffbeb" },
  archived:  { label: "Archived",  color: "#6B7280", bg: "#f3f4f6" },
};

const TYPE_CONFIG = {
  Handbook:   { color: PURPLE,    bg: "#EAF1F8" },
  Guideline:  { color: "#2196f3", bg: "#e3f2fd" },
  Newsletter: { color: "#16a34a", bg: "#f0fdf4" },
  Curriculum: { color: "#f59e0b", bg: "#fffbeb" },
  Other:      { color: "#6B7280", bg: "#f3f4f6" },
};

const FALLBACK_DOCS = [
  {
    id: "1",
    title: "Student Handbook 2025–26",
    description: "Comprehensive handbook containing school policies, conduct guidelines, and academic calendar for the 2025–26 year.",
    documentType: "Handbook",
    uploadedDate: "2025-01-05",
    status: "published",
    fileUrl: null,
    pageCount: 24,
  },
  {
    id: "2",
    title: "Tamil Curriculum Guidelines",
    description: "Detailed curriculum framework for all grades, including learning objectives and assessment criteria.",
    documentType: "Curriculum",
    uploadedDate: "2025-01-10",
    status: "published",
    fileUrl: null,
    pageCount: 18,
  },
  {
    id: "3",
    title: "Spring Newsletter 2025",
    description: "Community newsletter covering school events, achievements, and upcoming activities for Spring 2025.",
    documentType: "Newsletter",
    uploadedDate: "2025-02-20",
    status: "draft",
    fileUrl: null,
    pageCount: 8,
  },
];

const ROWS_PER_PAGE = 10;

const EMPTY_FORM = {
  title: "",
  description: "",
  documentType: "Handbook",
  status: "draft",
  file: null,
  fileName: "",
};

const AdminFlipbook = () => {
  const navigate = useNavigate();
  const [rows, setRows]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filterText, setFilterText] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [page, setPage]             = useState(1);
  const [uploadDialog, setUploadDialog] = useState({ open: false, editDoc: null });
  const [form, setForm]             = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null, title: "" });
  const [toast, setToast]           = useState({ open: false, severity: "success", message: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchData("admin/flipbook");
      const data = res?.data?.documents ?? res?.data ?? [];
      setRows(Array.isArray(data) ? data : []);
    } catch (_) {
      setRows([]);
      setToast({ open: true, severity: "warning", message: "Failed to load flipbook documents" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter(r => {
    if (!filterText) return true;
    const s = filterText.toLowerCase();
    return (
      (r.title || "").toLowerCase().includes(s) ||
      (r.documentType || "").toLowerCase().includes(s) ||
      (r.description || "").toLowerCase().includes(s)
    );
  });

  const pageCount = Math.ceil(filtered.length / ROWS_PER_PAGE);
  const pageRows  = filtered.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

  const openUpload = (editDoc = null) => {
    if (editDoc) {
      setForm({
        title:        editDoc.title || "",
        description:  editDoc.description || "",
        documentType: editDoc.documentType || "Handbook",
        status:       editDoc.status || "draft",
        file:         null,
        fileName:     "",
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setUploadDialog({ open: true, editDoc });
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setForm(f => ({ ...f, file, fileName: file.name }));
    }
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      setToast({ open: true, severity: "error", message: "Title is required." });
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("title",        form.title.trim());
      fd.append("description",  form.description.trim());
      fd.append("documentType", form.documentType);
      fd.append("status",       form.status);
      if (form.file) fd.append("flipbookFile", form.file);

      if (uploadDialog.editDoc) {
        await putData(`admin/flipbook/${uploadDialog.editDoc.id}`, fd);
        setToast({ open: true, severity: "success", message: "Document updated successfully!" });
      } else {
        await postData("admin/flipbook", fd);
        setToast({ open: true, severity: "success", message: "Document uploaded successfully!" });
      }
      setUploadDialog({ open: false, editDoc: null });
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      setToast({ open: true, severity: "error", message: err?.response?.data?.message || "Upload failed. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteData(`admin/flipbook/${confirmDelete.id}`);
      setToast({ open: true, severity: "success", message: "Document deleted successfully!" });
      setConfirmDelete({ open: false, id: null, title: "" });
      load();
    } catch (_) {
      setToast({ open: true, severity: "error", message: "Delete failed." });
    }
  };

  const handlePreview = (doc) => {
    navigate(`/flipbook/${doc.id}`);
  };

  const StatusChip = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
    return (
      <Chip label={cfg.label} size="small"
        sx={{ backgroundColor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: "11px", borderRadius: "20px" }} />
    );
  };

  const TypeChip = ({ type }) => {
    const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.Other;
    return (
      <Chip label={type || "—"} size="small"
        sx={{ backgroundColor: cfg.bg, color: cfg.color, fontSize: "11px" }} />
    );
  };

  return (
    <Box sx={{ p: 3, fontFamily: "'Inter', 'Roboto', sans-serif" }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity} message={toast.message} />

      <ConfirmDialog
        open={confirmDelete.open}
        title="Delete Document"
        message={`Are you sure you want to delete "${confirmDelete.title}"? This action cannot be undone.`}
        severity="danger"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete({ open: false, id: null, title: "" })}
      />

      {/* Page Header */}
      <Typography variant="body2" color="text.secondary" mb={0.5}>Pages / Flipbook Management</Typography>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <MenuBookIcon sx={{ color: PURPLE, fontSize: "28px" }} />
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "22px", color: "#111827" }}>Flipbook Documents</Typography>
            <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
              Manage and publish documents for the public flipbook viewer
            </Typography>
          </Box>
          <Chip label={`${rows.length} documents`} size="small"
            sx={{ backgroundColor: "#EAF1F8", color: PURPLE, fontWeight: 600, fontSize: "12px" }} />
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            startIcon={<FilterListIcon />} size="small" onClick={() => setShowFilter(v => !v)}
            sx={{ border: "1px solid #D5D7DA", borderRadius: "8px", color: "#8F95B2", textTransform: "none", fontSize: "13px" }}
          >
            Filters
          </Button>
          <Button
            startIcon={<FileDownloadOutlinedIcon />} size="small"
            sx={{ border: "1px solid #D5D7DA", borderRadius: "8px", color: "#8F95B2", textTransform: "none", fontSize: "13px" }}
          >
            Export
          </Button>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => openUpload()}
            sx={{
              background: `linear-gradient(90deg, ${SECONDARY}, ${PURPLE})`,
              color: "#fff", textTransform: "none", borderRadius: "8px",
              px: 2.5, fontSize: "13px", fontWeight: 600, boxShadow: "none",
            }}
          >
            Upload New Document
          </Button>
        </Box>
      </Box>

      {/* Filter */}
      {showFilter && (
        <Box sx={{ mb: 2 }}>
          <TextField
            size="small" fullWidth
            placeholder="Search by title, description, document type..."
            value={filterText} onChange={e => { setFilterText(e.target.value); setPage(1); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#8F95B2", fontSize: "18px" }} /></InputAdornment> }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
          />
        </Box>
      )}

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: "16px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: PURPLE }} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead sx={{ background: `linear-gradient(135deg, ${PURPLE} 0%, ${SECONDARY} 100%)` }}>
                  <TableRow>
                    {["#", "Title", "Description", "Document Type", "Uploaded Date", "Pages", "Status", "Actions"].map(h => (
                      <TableCell key={h} sx={{ color: "#fff", fontWeight: 600, fontSize: "12px", whiteSpace: "nowrap" }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ textAlign: "center", py: 6, color: "#8F95B2", fontSize: "14px" }}>
                        No flipbook documents found. Upload your first document!
                      </TableCell>
                    </TableRow>
                  ) : pageRows.map((doc, idx) => (
                    <TableRow key={doc.id || idx} sx={{ "&:hover": { backgroundColor: "#f9fafb" } }}>
                      <TableCell sx={{ fontSize: "13px", color: "#6B7280" }}>{(page - 1) * ROWS_PER_PAGE + idx + 1}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{
                            width: 34, height: 34, borderRadius: "8px",
                            backgroundColor: "#EAF1F8",
                            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0
                          }}>
                            <MenuBookIcon sx={{ color: PURPLE, fontSize: "16px" }} />
                          </Box>
                          <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>
                            {doc.title}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: "12px", color: "#6B7280", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {doc.description || "—"}
                      </TableCell>
                      <TableCell><TypeChip type={doc.documentType} /></TableCell>
                      <TableCell sx={{ fontSize: "12px", color: "#374151" }}>
                        {doc.uploadedDate ? new Date(doc.uploadedDate).toLocaleDateString("en-GB") : "—"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "13px", color: "#374151", textAlign: "center" }}>
                        {doc.pageCount || "—"}
                      </TableCell>
                      <TableCell><StatusChip status={doc.status} /></TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <Tooltip title="Preview Flipbook">
                            <IconButton size="small" onClick={() => handlePreview(doc)}
                              sx={{ color: PURPLE, "&:hover": { backgroundColor: "#EAF1F8" } }}>
                              <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openUpload(doc)}
                              sx={{ color: "#2196f3", "&:hover": { backgroundColor: "#e3f2fd" } }}>
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small"
                              onClick={() => setConfirmDelete({ open: true, id: doc.id, title: doc.title })}
                              sx={{ color: "#dc2626", "&:hover": { backgroundColor: "#fef2f2" } }}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {pageCount > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <Pagination count={pageCount} page={page} onChange={(_, v) => setPage(v)} size="small"
                  sx={{ "& .MuiPaginationItem-root.Mui-selected": { backgroundColor: PURPLE, color: "#fff" } }} />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Upload / Edit Dialog */}
      <Dialog
        open={uploadDialog.open}
        onClose={() => { setUploadDialog({ open: false, editDoc: null }); setForm(EMPTY_FORM); }}
        maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box sx={{
            background: `linear-gradient(135deg, ${PURPLE} 0%, ${SECONDARY} 100%)`,
            px: 3, py: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center"
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <UploadFileIcon sx={{ color: "#fff", fontSize: "22px" }} />
              <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "17px" }}>
                {uploadDialog.editDoc ? "Edit Document" : "Upload New Document"}
              </Typography>
            </Box>
            <IconButton size="small"
              onClick={() => { setUploadDialog({ open: false, editDoc: null }); setForm(EMPTY_FORM); }}
              sx={{ color: "#fff" }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2.5}>
            {/* Title */}
            <Grid item xs={12}>
              <TextField
                fullWidth size="small"
                label="Document Title"
                placeholder="e.g. Student Handbook 2025–26"
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                required
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" multiline rows={3}
                label="Description"
                placeholder="Brief description of this document..."
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Grid>

            {/* Document Type */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Document Type</InputLabel>
                <Select
                  value={form.documentType}
                  onChange={e => setForm(f => ({ ...f, documentType: e.target.value }))}
                  label="Document Type"
                  sx={{ borderRadius: "8px" }}
                >
                  {DOC_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            {/* Status */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={form.status}
                  onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                  label="Status"
                  sx={{ borderRadius: "8px" }}
                >
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="published">Published</MenuItem>
                  <MenuItem value="archived">Archived</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* File Upload */}
            <Grid item xs={12}>
              <Divider sx={{ mb: 1.5 }} />
              <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#374151", mb: 1 }}>
                Upload File
              </Typography>
              <Box
                sx={{
                  border: "2px dashed #D1D5DB",
                  borderRadius: "12px",
                  p: 3,
                  textAlign: "center",
                  cursor: "pointer",
                  backgroundColor: "#fafafa",
                  transition: "all 0.2s",
                  "&:hover": { borderColor: PURPLE, backgroundColor: "#faf5ff" },
                  position: "relative",
                }}
                onClick={() => window.document.getElementById("flipbook-file-input").click()}
              >
                <input
                  id="flipbook-file-input"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
                {form.fileName ? (
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                    <CheckCircleIcon sx={{ color: "#16a34a", fontSize: "32px" }} />
                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>
                      {form.fileName}
                    </Typography>
                    <Typography sx={{ fontSize: "11px", color: "#9CA3AF" }}>
                      Click to replace
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                    <UploadFileIcon sx={{ color: PURPLE, fontSize: "32px" }} />
                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#374151" }}>
                      Click to upload or drag &amp; drop
                    </Typography>
                    <Typography sx={{ fontSize: "11px", color: "#9CA3AF" }}>
                      PDF, JPG, or PNG — max 20MB
                    </Typography>
                  </Box>
                )}
              </Box>
              {uploadDialog.editDoc && !form.fileName && (
                <Typography sx={{ fontSize: "11px", color: "#9CA3AF", mt: 0.75 }}>
                  Leave empty to keep the existing file.
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={() => { setUploadDialog({ open: false, editDoc: null }); setForm(EMPTY_FORM); }}
            sx={{ border: "1.5px solid #D1D5DB", borderRadius: "10px", textTransform: "none", color: "#374151", px: 3 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || !form.title.trim()}
            sx={{
              background: `linear-gradient(90deg, ${SECONDARY}, ${PURPLE})`,
              borderRadius: "10px", textTransform: "none", fontWeight: 600,
              boxShadow: "none", px: 3,
            }}
          >
            {submitting
              ? <CircularProgress size={18} sx={{ color: "#fff" }} />
              : uploadDialog.editDoc ? "Save Changes" : "Upload Document"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminFlipbook;
